import React, { useState, useEffect, useRef } from 'react';
import { Server, Play, CheckCircle, XCircle, Loader, AlertTriangle, Copy, ExternalLink, Terminal, Bot, Settings, MessageSquare, Send, StopCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import { getBrandName } from '../../config/branding';

const InstanceDeployment = () => {
  // Form state
  const [formData, setFormData] = useState({
    customer_name: '',
    admin_email: '',
    admin_password: '',
    netsuite_account_id: '',
    customer_email: '',
    sharepoint_tenant: '',
    openai_api_key: '',
    anthropic_api_key: ''
  });

  // UI state
  const [step, setStep] = useState('form'); // 'form', 'deploying', 'claude_chat', 'success', 'error'
  const [interactiveMode, setInteractiveMode] = useState(true); // Default to interactive
  const [deploymentLogs, setDeploymentLogs] = useState([]);
  const [deploymentProgress, setDeploymentProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [currentPhase, setCurrentPhase] = useState(1);
  const [deploymentResult, setDeploymentResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [deploymentId, setDeploymentId] = useState(null);

  // Claude Chat state
  const [claudePrompt, setClaudePrompt] = useState('');
  const [claudeOutput, setClaudeOutput] = useState([]);
  const [chatMessage, setChatMessage] = useState('');
  const [isClaudeRunning, setIsClaudeRunning] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  const logsEndRef = useRef(null);
  const claudeOutputRef = useRef(null);
  const pollingRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [deploymentLogs]);

  useEffect(() => {
    claudeOutputRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [claudeOutput]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Customer name is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.customer_name)) {
      newErrors.customer_name = 'Only lowercase letters, numbers, and hyphens allowed';
    } else if (formData.customer_name.length < 3) {
      newErrors.customer_name = 'Customer name must be at least 3 characters';
    }
    if (!formData.admin_email.trim()) {
      newErrors.admin_email = 'Admin email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
      newErrors.admin_email = 'Invalid email format';
    }
    if (!formData.admin_password) {
      newErrors.admin_password = 'Admin password is required';
    } else if (formData.admin_password.length < 8) {
      newErrors.admin_password = 'Password must be at least 8 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setDeploymentLogs(prev => [...prev, { timestamp, message, type }]);
  };

  const addClaudeOutput = (text, type = 'output') => {
    setClaudeOutput(prev => [...prev, { text, type, timestamp: new Date().toLocaleTimeString() }]);
  };

  // Start deployment - either interactive or automatic
  const startDeployment = async () => {
    if (!validateForm()) {
      toast.error('Please fix the form errors');
      return;
    }

    setStep('deploying');
    setDeploymentLogs([]);
    setDeploymentProgress(0);
    setCurrentPhase(1);
    setCurrentStep('Initializing deployment...');

    addLog('Starting deployment process...', 'info');
    addLog(`Mode: ${interactiveMode ? 'Interactive (Chat with Claude)' : 'Automatic'}`, 'info');

    try {
      const endpoint = interactiveMode ? '/api/admin/deploy-phase1' : '/api/admin/deploy-instance';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('sankalvax_token')}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setDeploymentId(data.deployment_id);
        addLog(`Deployment started: ${data.deployment_id}`, 'success');
        pollDeploymentStatus(data.deployment_id);
      } else {
        addLog(data.error || data.detail || 'Deployment failed to start', 'error');
        setDeploymentResult({ error: data.error || data.detail });
        setStep('error');
      }
    } catch (error) {
      addLog(`Deployment error: ${error.message}`, 'error');
      setDeploymentResult({ error: error.message });
      setStep('error');
    }
  };

  const pollDeploymentStatus = async (depId) => {
    let lastLogCount = 0;

    const poll = async () => {
      try {
        const response = await fetch(`/api/admin/deploy-status/${depId}`, {
          headers: { 'Authorization': `Bearer ${Cookies.get('sankalvax_token')}` }
        });
        const data = await response.json();

        if (data.logs && data.logs.length > lastLogCount) {
          const newLogs = data.logs.slice(lastLogCount);
          newLogs.forEach(log => addLog(log.message, log.level || 'info'));
          lastLogCount = data.logs.length;
        }

        setDeploymentProgress(data.progress || 0);
        setCurrentStep(data.current_step || '');
        setCurrentPhase(data.progress <= 40 ? 1 : 2);

        if (data.status === 'completed') {
          addLog('Deployment completed successfully!', 'success');
          setDeploymentResult(data.result);
          setStep('success');
        } else if (data.status === 'failed') {
          addLog(`Deployment failed: ${data.error}`, 'error');
          setDeploymentResult({ error: data.error });
          setStep('error');
        } else if (data.status === 'waiting_claude' && interactiveMode) {
          // Phase 1 complete, switch to Claude chat
          addLog('Phase 1 complete! Ready for Claude Code interaction.', 'success');
          setClaudePrompt(data.claude_prompt || '');
          setStep('claude_chat');
          connectWebSocket(depId);
        } else {
          pollingRef.current = setTimeout(poll, 2000);
        }
      } catch (error) {
        console.error('Polling error:', error);
        pollingRef.current = setTimeout(poll, 5000);
      }
    };

    poll();
  };

  // WebSocket for Claude Code chat
  const connectWebSocket = (depId) => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/api/admin/ws/claude-chat/${depId}`;

    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      setWsConnected(true);
      addClaudeOutput('Connected to Claude Code session', 'system');
    };

    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'ping':
          // Respond to server ping with pong to keep connection alive
          wsRef.current?.send(JSON.stringify({ type: 'pong' }));
          break;
        case 'pong':
          // Server responded to our ping, connection is alive
          break;
        case 'connected':
          addClaudeOutput(`Working directory: ${data.working_dir}`, 'system');
          break;
        case 'prepared_prompt':
          setClaudePrompt(data.prompt);
          break;
        case 'claude_started':
          setIsClaudeRunning(true);
          addClaudeOutput('Claude Code started...', 'system');
          break;
        case 'claude_output':
          addClaudeOutput(data.text, 'output');
          break;
        case 'claude_finished':
          setIsClaudeRunning(false);
          if (data.success) {
            addClaudeOutput('Claude Code completed successfully!', 'success');
            setDeploymentProgress(100);
          } else {
            addClaudeOutput(`Claude Code failed with exit code ${data.exit_code}`, 'error');
          }
          break;
        case 'error':
          addClaudeOutput(`Error: ${data.message}`, 'error');
          break;
        case 'cancelled':
          setIsClaudeRunning(false);
          addClaudeOutput('Claude Code cancelled', 'warning');
          break;
        default:
          break;
      }
    };

    wsRef.current.onclose = () => {
      setWsConnected(false);
      addClaudeOutput('Disconnected from Claude Code session', 'system');
    };

    wsRef.current.onerror = () => {
      addClaudeOutput('WebSocket connection error', 'error');
    };
  };

  const runClaudePrompt = () => {
    if (!wsRef.current || !wsConnected) {
      toast.error('Not connected to Claude Code');
      return;
    }
    wsRef.current.send(JSON.stringify({
      type: 'run_prompt',
      prompt: claudePrompt
    }));
  };

  const sendChatMessage = () => {
    if (!chatMessage.trim() || !wsRef.current || !wsConnected) return;

    addClaudeOutput(`You: ${chatMessage}`, 'user');
    wsRef.current.send(JSON.stringify({
      type: 'send_message',
      message: chatMessage
    }));
    setChatMessage('');
  };

  const cancelClaude = () => {
    if (wsRef.current && wsConnected) {
      wsRef.current.send(JSON.stringify({ type: 'cancel' }));
    }
  };

  const finishDeployment = () => {
    // Mark as complete and go to success
    setDeploymentResult({
      customer_name: formData.customer_name,
      port: deploymentProgress >= 100 ? 'configured' : 'pending',
      instance_path: `/opt/client-instances/${formData.customer_name}`,
      database: `sankalvax_${formData.customer_name.replace(/-/g, '_')}`
    });
    setStep('success');
    if (wsRef.current) wsRef.current.close();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // ============================================
  // RENDER: Form
  // ============================================
  const renderForm = () => (
    <div className="space-y-6">
      {/* Mode Selection Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex gap-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Terminal className="w-5 h-5 text-blue-600" />
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <Bot className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Hybrid Deployment</h3>
              <p className="text-sm text-gray-600">
                Phase 1: Shell Script → Phase 2: Claude Code
              </p>
            </div>
          </div>

          {/* Interactive Mode Toggle */}
          <label className="flex items-center gap-3 cursor-pointer">
            <span className="text-sm text-gray-600">Interactive Mode</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={interactiveMode}
                onChange={(e) => setInteractiveMode(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-11 h-6 rounded-full transition-colors ${interactiveMode ? 'bg-purple-600' : 'bg-gray-300'}`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${interactiveMode ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`} />
              </div>
            </div>
            <MessageSquare className={`w-4 h-4 ${interactiveMode ? 'text-purple-600' : 'text-gray-400'}`} />
          </label>
        </div>

        {interactiveMode && (
          <p className="mt-2 text-xs text-purple-600 bg-purple-100 rounded px-2 py-1 inline-block">
            Chat with Claude Code after Phase 1 to customize the deployment
          </p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Server className="w-8 h-8 text-blue-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Deploy New Instance</h2>
            <p className="text-sm text-gray-500">Create a new customer instance of {getBrandName()} Platform</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Required Fields */}
          <div className="border-b pb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Required Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleInputChange}
                  placeholder="e.g., acme"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.customer_name ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.customer_name && <p className="mt-1 text-xs text-red-500">{errors.customer_name}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  Used for: subdomain (acme.nsgruve.ai.pocsolution.com), path (/opt/client-instances/acme)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admin Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="admin_email"
                  value={formData.admin_email}
                  onChange={handleInputChange}
                  placeholder="admin@customer.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.admin_email ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.admin_email && <p className="mt-1 text-xs text-red-500">{errors.admin_email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admin Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="admin_password"
                  value={formData.admin_password}
                  onChange={handleInputChange}
                  placeholder="Minimum 8 characters"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.admin_password ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.admin_password && <p className="mt-1 text-xs text-red-500">{errors.admin_password}</p>}
              </div>
            </div>

            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                <span><strong>Auto-configured:</strong> Port, nginx, systemd, .env file</span>
              </p>
            </div>
          </div>

          {/* Optional Fields */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-4">Optional Configuration</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">NetSuite Account ID</label>
                <input
                  type="text"
                  name="netsuite_account_id"
                  value={formData.netsuite_account_id}
                  onChange={handleInputChange}
                  placeholder="e.g., 11315770"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Email</label>
                <input
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleInputChange}
                  placeholder="user@customer.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              onClick={startDeployment}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Play className="w-4 h-4" />
              {interactiveMode ? 'Start Interactive Deployment' : 'Start Deployment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================
  // RENDER: Deploying (Phase 1)
  // ============================================
  const renderDeploying = () => (
    <div className="space-y-6">
      {/* Phase Indicators */}
      <div className="grid grid-cols-2 gap-4">
        <div className={`p-4 rounded-lg border-2 ${currentPhase === 1 ? 'border-blue-500 bg-blue-50' : 'border-green-500 bg-green-50'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${currentPhase === 1 ? 'bg-blue-500' : 'bg-green-500'}`}>
              {currentPhase === 1 ? <Loader className="w-5 h-5 text-white animate-spin" /> : <CheckCircle className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Phase 1: Shell Script</h3>
              <p className="text-xs text-gray-500">Copy files, database, venv</p>
            </div>
          </div>
        </div>

        <div className={`p-4 rounded-lg border-2 ${currentPhase === 2 ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${currentPhase === 2 ? 'bg-purple-500' : 'bg-gray-300'}`}>
              {interactiveMode ? <MessageSquare className="w-5 h-5 text-white" /> : <Bot className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Phase 2: Claude Code</h3>
              <p className="text-xs text-gray-500">{interactiveMode ? 'Interactive chat' : 'Auto-configure'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Running Phase 1...</h2>
            <p className="text-sm text-gray-500">{currentStep}</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{deploymentProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div className="bg-blue-600 h-3 rounded-full transition-all duration-500" style={{ width: `${deploymentProgress}%` }} />
          </div>
        </div>

        {/* Logs */}
        <div className="bg-gray-900 rounded-lg p-4 h-64 overflow-y-auto font-mono text-sm">
          {deploymentLogs.map((log, index) => (
            <div key={index} className={`mb-1 ${log.type === 'error' ? 'text-red-400' : log.type === 'success' ? 'text-green-400' : 'text-gray-300'}`}>
              <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
            </div>
          ))}
          <div ref={logsEndRef} />
        </div>
      </div>
    </div>
  );

  // ============================================
  // RENDER: Claude Code Chat
  // ============================================
  const renderClaudeChat = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bot className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold">Claude Code Interactive</h2>
              <p className="text-purple-200 text-sm">Phase 2: Configure your instance</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-400' : 'bg-red-400'}`} />
            <span className="text-sm">{wsConnected ? 'Connected' : 'Disconnected'}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Prompt Panel */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Terminal className="w-5 h-5 text-purple-600" />
              Prepared Prompt
            </h3>
            <div className="flex gap-2">
              <button
                onClick={runClaudePrompt}
                disabled={isClaudeRunning || !wsConnected}
                className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
              >
                <Play className="w-4 h-4" />
                Run
              </button>
              {isClaudeRunning && (
                <button
                  onClick={cancelClaude}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center gap-1"
                >
                  <StopCircle className="w-4 h-4" />
                  Cancel
                </button>
              )}
            </div>
          </div>
          <textarea
            value={claudePrompt}
            onChange={(e) => setClaudePrompt(e.target.value)}
            className="w-full h-80 p-3 border border-gray-300 rounded-lg font-mono text-xs resize-none focus:ring-2 focus:ring-purple-500"
            placeholder="Claude Code prompt will appear here..."
          />
        </div>

        {/* Output Panel */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h3 className="font-medium text-gray-900 flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            Claude Output
          </h3>
          <div className="bg-gray-900 rounded-lg p-4 h-80 overflow-y-auto font-mono text-sm">
            {claudeOutput.map((item, index) => {
              const text = item.text || '';

              // Determine styling based on content
              let className = 'whitespace-pre-wrap break-words ';
              let style = {};

              if (item.type === 'error') {
                className += 'text-red-400';
              } else if (item.type === 'success') {
                className += 'text-green-400';
              } else if (item.type === 'user') {
                className += 'text-blue-400';
              } else if (item.type === 'system') {
                className += 'text-yellow-400';
              } else if (text.startsWith('📖') || text.startsWith('📝') || text.startsWith('✏️')) {
                // File operations - cyan
                className += 'text-cyan-400 font-semibold';
              } else if (text.startsWith('⚙️') || text.startsWith('$')) {
                // Bash commands - yellow/amber
                className += 'text-amber-400';
                if (text.startsWith('$')) {
                  style = { backgroundColor: 'rgba(251, 191, 36, 0.1)', padding: '2px 4px', borderRadius: '2px' };
                }
              } else if (text.startsWith('🔍')) {
                // Search operations - blue
                className += 'text-blue-400';
              } else if (text.startsWith('🚀') || text.startsWith('🔧')) {
                // Agent/tool operations - purple
                className += 'text-purple-400';
              } else if (text.startsWith('📋')) {
                // Todo - orange
                className += 'text-orange-400';
              } else if (text.startsWith('✓') || text.includes('✓')) {
                // Success indicator - green
                className += 'text-green-400';
              } else if (text.startsWith('❌') || text.includes('❌')) {
                // Error indicator - red
                className += 'text-red-400';
              } else if (text.startsWith('ℹ️')) {
                // Info - gray/white
                className += 'text-gray-400';
              } else if (text.startsWith('━')) {
                // Separator line
                className += 'text-gray-600';
              } else if (text.startsWith('   ')) {
                // Indented output (tool results)
                className += 'text-gray-400';
              } else {
                // Default assistant text - white
                className += 'text-gray-200';
              }

              return (
                <div key={index} className={className} style={style}>
                  {text}
                </div>
              );
            })}
            <div ref={claudeOutputRef} />
          </div>

          {/* Chat Input */}
          <div className="mt-4 flex gap-2">
            <input
              type="text"
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
              placeholder="Send follow-up instructions..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              disabled={!wsConnected}
            />
            <button
              onClick={sendChatMessage}
              disabled={!chatMessage.trim() || !wsConnected}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <button
          onClick={finishDeployment}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
        >
          <CheckCircle className="w-4 h-4" />
          Finish Deployment
        </button>
      </div>
    </div>
  );

  // ============================================
  // RENDER: Success
  // ============================================
  const renderSuccess = () => {
    const result = deploymentResult || {};
    const port = result.port || 'auto-assigned';
    const targetPath = result.instance_path || `/opt/client-instances/${formData.customer_name}`;
    const database = result.database || `sankalvax_${formData.customer_name.replace(/-/g, '_')}`;

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500 rounded-lg">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-medium text-green-900">Deployment Completed</h3>
              <p className="text-sm text-green-700">{formData.customer_name}.nsgruve.ai.pocsolution.com</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Instance Details</h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">URL:</span>
              <code className="bg-gray-100 px-2 py-1 rounded">https://{formData.customer_name}.nsgruve.ai.pocsolution.com</code>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Path:</span>
              <code className="bg-gray-100 px-2 py-1 rounded">{targetPath}</code>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Database:</span>
              <code className="bg-gray-100 px-2 py-1 rounded">{database}</code>
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <button
              onClick={() => {
                setStep('form');
                setFormData({ customer_name: '', admin_email: '', admin_password: '', netsuite_account_id: '', customer_email: '', sharepoint_tenant: '', openai_api_key: '', anthropic_api_key: '' });
                setDeploymentLogs([]);
                setClaudeOutput([]);
                setDeploymentResult(null);
              }}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Deploy Another
            </button>
            <a
              href={`https://${formData.customer_name}.nsgruve.ai.pocsolution.com`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Open Instance
            </a>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER: Error
  // ============================================
  const renderError = () => (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <XCircle className="w-6 h-6 text-red-500" />
          <div>
            <h3 className="font-medium text-red-900">Deployment Failed</h3>
            <p className="text-sm text-red-700">{deploymentResult?.error || 'Unknown error'}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="bg-gray-900 rounded-lg p-4 h-64 overflow-y-auto font-mono text-sm mb-4">
          {deploymentLogs.map((log, index) => (
            <div key={index} className={`mb-1 ${log.type === 'error' ? 'text-red-400' : 'text-gray-300'}`}>
              <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3">
          <button onClick={() => setStep('form')} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
            Back
          </button>
          <button onClick={startDeployment} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Retry
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {step === 'form' && renderForm()}
      {step === 'deploying' && renderDeploying()}
      {step === 'claude_chat' && renderClaudeChat()}
      {step === 'success' && renderSuccess()}
      {step === 'error' && renderError()}
    </div>
  );
};

export default InstanceDeployment;
